'use client'

import type { CSSProperties } from 'react'
import { useCallback, useRef, useState } from 'react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'

import { useGraftcodeOnboardingStore } from '@apps/graftcode-onboarding/stores/graftcode-onboarding.store'

import {
  GRAFTCODE_ONBOARDING_ASSETS_BASE,
  GRAFTCODE_ONBOARDING_SCENARIOS,
  type GraftcodeOnboardingScenarioId,
} from '../../graftcode-onboarding.const'
import { GRAFTCODE_ONBOARDING_TEST_IDS } from '../../graftcode-onboarding-test-ids.const'
import { prefetchOnboardingGuideForScenario } from '../../lib/onboarding-tutorial-content-cache'

import styles from './GraftcodeOnboardingWizard.module.scss'

type GraftcodeOnboardingScenarioStepProps = {
  onBack: () => void
  onSelectScenario: (scenarioId: GraftcodeOnboardingScenarioId) => void
}

export function GraftcodeOnboardingScenarioStep({ onBack, onSelectScenario }: GraftcodeOnboardingScenarioStepProps) {
  const t = useTranslations('graftcodeOnboarding')
  const goalId = useGraftcodeOnboardingStore((state) => state.goalId)
  const [hint, setHint] = useState(() => t(GRAFTCODE_ONBOARDING_SCENARIOS[0].hintKey))
  const [pendingScenarioId, setPendingScenarioId] = useState<GraftcodeOnboardingScenarioId | null>(null)
  const selectingRef = useRef(false)

  const prefetchScenario = useCallback(
    (scenarioId: GraftcodeOnboardingScenarioId) => {
      void prefetchOnboardingGuideForScenario(scenarioId, goalId).catch(() => undefined)
    },
    [goalId]
  )

  const handleSelectScenario = useCallback(
    async (scenarioId: GraftcodeOnboardingScenarioId) => {
      if (selectingRef.current) {
        return
      }
      selectingRef.current = true
      setPendingScenarioId(scenarioId)
      try {
        await prefetchOnboardingGuideForScenario(scenarioId, goalId)
        onSelectScenario(scenarioId)
      } catch {
        onSelectScenario(scenarioId)
      } finally {
        selectingRef.current = false
        setPendingScenarioId(null)
      }
    },
    [goalId, onSelectScenario]
  )

  const rows = [GRAFTCODE_ONBOARDING_SCENARIOS.slice(0, 3), GRAFTCODE_ONBOARDING_SCENARIOS.slice(3, 6)]

  return (
    <div className={styles.main}>
      <div className={styles.content}>
        <h1
          className={`${styles.title} ${styles.reveal}`}
          id="graftcode-onboarding-title"
          style={{ '--ob-i': 0 } as CSSProperties}
        >
          {t('wizard.scenarioTitle')}
        </h1>
        <div className={styles.scenarios} role="group" aria-labelledby="graftcode-onboarding-title">
          {rows.map((row) => (
            <div key={row.map((scenario) => scenario.id).join('-')} className={styles.scenarioRow}>
              {row.map((scenario) => {
                const globalIndex = GRAFTCODE_ONBOARDING_SCENARIOS.findIndex((item) => item.id === scenario.id)
                const isPending = pendingScenarioId === scenario.id
                return (
                  <button
                    key={scenario.id}
                    type="button"
                    className={`${styles.scenarioCard} ${styles.reveal}`}
                    style={{ '--ob-i': globalIndex + 1 } as CSSProperties}
                    data-testid={GRAFTCODE_ONBOARDING_TEST_IDS.scenarioCard(scenario.id)}
                    disabled={Boolean(pendingScenarioId)}
                    aria-busy={isPending}
                    onFocus={() => {
                      setHint(t(scenario.hintKey))
                      prefetchScenario(scenario.id)
                    }}
                    onMouseEnter={() => {
                      setHint(t(scenario.hintKey))
                      prefetchScenario(scenario.id)
                    }}
                    onClick={() => void handleSelectScenario(scenario.id)}
                  >
                    <span className={styles.scenarioIcon}>
                      <Image src={scenario.icon} alt="" width={24} height={24} />
                    </span>
                    <span>{t(scenario.labelKey)}</span>
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      </div>
      <p className={styles.hint} id="graftcode-onboarding-hint" aria-live="polite">
        {pendingScenarioId ? t('dashboard.loading') : hint}
      </p>
      <div className={`${styles.nav} ${styles.reveal}`} style={{ '--ob-i': 7 } as CSSProperties}>
        <button
          type="button"
          className={styles.backButton}
          data-testid={GRAFTCODE_ONBOARDING_TEST_IDS.back}
          disabled={Boolean(pendingScenarioId)}
          onClick={onBack}
        >
          <span className={styles.backIcon}>
            <Image src={`${GRAFTCODE_ONBOARDING_ASSETS_BASE}/arrow-left.svg`} alt="" width={20} height={20} />
          </span>
          {t('wizard.back')}
        </button>
      </div>
    </div>
  )
}
